import { useStoreState } from "ezStore";
import { intl } from "LocaleWrapper";

import { FilterTypes } from "@cofano/cck";

import ShipSelect, { ShipSelectOption } from "view/ships/components/selects/ShipSelect";
import VoyageSelect, { VoyageSelectOption } from "components/selects/VoyageSelect";
import LocationSelect, { LocationSelectOption } from "view/locations/components/selects/LocationSelect";
import RelationSelect, { RelationSelectOption } from "view/relations/components/selects/RelationSelect";
import StatusSelect, { StatusSelectOption } from "view/statuses/components/selects/StatusSelect";
import ContainerTypeSelect, { ContainerTypeSelectOption } from "view/containertypes/components/selects/ContainerTypeSelect";

import { DataFormat, hasPluginPermission } from "imports/Utils";

import { RelationType } from "__generated__/enums";

const useCargoTable = () => {
	const session = useStoreState((state) => state.session);

	const { statuses } = session;

	const breakPoints = {
		xs: 12,
		sm: 12,
		md: 12,
		lg: 6,
		xl: 3,
	};

	return {
		getSearchInput: (filter: Record<string, unknown>) => {
			return {
				relations: filter.relations ? (filter.relations as RelationSelectOption[]).map(it => it.id) : undefined,
				charterNumber: filter.charterNumber ? filter.charterNumber as string : undefined,
				customerReference: filter.customerReference ? filter.customerReference as string : undefined,
				ships: filter.ships ? (filter.ships as ShipSelectOption[]).map(it => it.id) : undefined,
				voyages: filter.voyages ? (filter.voyages as VoyageSelectOption[]).map(it => it.id) : undefined,
				loadingLocations: filter.loadingLocations ? (filter.loadingLocations as LocationSelectOption[]).map(it => it.id) : undefined,
				dischargingLocations: filter.dischargingLocations ? (filter.dischargingLocations as LocationSelectOption[]).map(it => it.id) : undefined,
				currentStatuses: filter.currentStatuses ? (filter.currentStatuses as StatusSelectOption[]).map(it => it.id) : undefined,
				containerNumber: filter.containerNumber ? filter.containerNumber as string : undefined,
				containerType: filter.containerType ? (filter.containerType as ContainerTypeSelectOption).isoTypeCode : undefined,
				isEmpty: filter.isEmpty ? filter.isEmpty as boolean : undefined,
				loadingReference: filter.loadingReference ? filter.loadingReference as string : undefined,
				dischargingReference: filter.dischargingReference ? filter.dischargingReference as string : undefined,
				connectingVisitLoadingCallReferenceNumber: filter.connectingVisitLoadingCallReferenceNumber ? filter.connectingVisitLoadingCallReferenceNumber as string : undefined,
				connectingVisitDischargingCallReferenceNumber: filter.connectingVisitDischargingCallReferenceNumber ? filter.connectingVisitDischargingCallReferenceNumber as string : undefined,
				hasDangerousGoods: filter.hasDangerousGoods ? filter.hasDangerousGoods as boolean : undefined,
			};
		},

		filterFields: {
			Relations: {
				id: "relations",
				title: intl.formatMessage({ id: "CLIENT" }),
				render: (value: RelationSelectOption[] | null, onFieldChange: (options: RelationSelectOption[] | null) => void) => (
					<RelationSelect
						value={value}
						onChange={(options) => onFieldChange(options)}
						types={[ RelationType.Customer ]}
						multiple />
				),
				renderFilterValue: (value: RelationSelectOption[]) => value?.map(item => item.name).join(", "),
				breakPoints: breakPoints,
			},
			CharterNumber: {
				id: "charterNumber",
				title: intl.formatMessage({ id: "CHARTER_ORDER_NUMBER" }),
				filterType: FilterTypes.TEXT,
				renderFilterValue: (value: string) => value,
				breakPoints: breakPoints,
			},
			CustomerReference: {
				id: "customerReference",
				title: intl.formatMessage({ id: "CUSTOMER_REFERENCE" }),
				filterType: FilterTypes.TEXT,
				renderFilterValue: (value: string) => value,
				breakPoints: breakPoints,
			},
			Ships: {
				id: "ships",
				title: intl.formatMessage({ id: "VOYAGE_SHIPS" }),
				render: (value: ShipSelectOption[] | null, onFieldChange: (options: ShipSelectOption[] | null) => void) => (
					<ShipSelect
						value={value}
						onChange={(options) => onFieldChange(options)}
						multiple />
				),
				renderFilterValue: (value: ShipSelectOption[]) => value?.map(item => item.name).join(", "),
				breakPoints: breakPoints,
			},
			Voyages: {
				id: "voyages",
				title: intl.formatMessage({ id: "VOYAGE_NUMBER" }),
				render: (value: VoyageSelectOption[] | null, onFieldChange: (options: VoyageSelectOption[] | null) => void) => (
					<VoyageSelect
						value={value}
						onChange={(options) => onFieldChange(options)}
						includeExpired
						multiple />
				),
				renderFilterValue: (value: VoyageSelectOption[]) => value?.map(item => item.voyageNumber).join(", "),
				breakPoints: breakPoints,
			},
			LoadingLocations: {
				id: "loadingLocations",
				title: intl.formatMessage({ id: "ORDER_LOADING_PLACE" }),
				render: (value: LocationSelectOption[] | null, onFieldChange: (options: LocationSelectOption[] | null) => void) => (
					<LocationSelect
						value={value}
						onChange={(options) => onFieldChange(options)}
						multiple />
				),
				renderFilterValue: (value: LocationSelectOption[]) => value?.map(item => item.description).join(", "),
				breakPoints: breakPoints,
			},
			DischargingLocations: {
				id: "dischargingLocations",
				title: intl.formatMessage({ id: "ORDER_DISCHARGING_PLACE" }),
				render: (value: LocationSelectOption[] | null, onFieldChange: (options: LocationSelectOption[] | null) => void) => (
					<LocationSelect
						value={value}
						onChange={(options) => onFieldChange(options)}
						multiple />
				),
				renderFilterValue: (value: LocationSelectOption[]) => value?.map(item => item.description).join(", "),
				breakPoints: breakPoints,
			},
			CurrentStatuses: {
				id: "currentStatuses",
				title: intl.formatMessage({ id: "STATUS" }),
				render: (value: StatusSelectOption[] | null, onFieldChange: (options: StatusSelectOption[] | null) => void) => (
					<StatusSelect
						value={value ?? undefined}
						onChange={(options) => onFieldChange(options ?? null)}
						statuses={statuses?.CARGO ?? []}
						multiple />
				),
				renderFilterValue: (value: StatusSelectOption[]) => value?.map(item => item.description).join(", "),
				disabled: !hasPluginPermission(session, "cargoStatuses"),
				breakPoints: breakPoints,
			},
			ContainerNumber: {
				id: "containerNumber",
				title: intl.formatMessage({ id: "CONTAINER_NUMBER" }),
				filterType: FilterTypes.TEXT,
				renderFilterValue: (value: string) => value,
				breakPoints: breakPoints,
			},
			ContainerType: {
				id: "containerType",
				title: intl.formatMessage({ id: "CARGO_CONTAINER_TYPE" }),
				render: (value: ContainerTypeSelectOption | null, onFieldChange: (options: ContainerTypeSelectOption | null) => void) => (
					<ContainerTypeSelect
						value={value}
						onChange={(option) => onFieldChange(option)} />
				),
				renderFilterValue: (value: ContainerTypeSelectOption) => DataFormat.containerType(value),
				breakPoints: breakPoints,
			},
			Empty: {
				id: "isEmpty",
				title: intl.formatMessage({ id: "EMPTY" }),
				filterType: FilterTypes.BOOLEAN,
				renderFilterValue: (value: boolean) => value ? intl.formatMessage({ id: "CARGO_EMPTY" }) : intl.formatMessage({ id: "CARGO_FULL" }),
				breakPoints: breakPoints,
			},
			LoadingReference: {
				id: "loadingReference",
				title: intl.formatMessage({ id: "LOADING_REFERENCE" }),
				filterType: FilterTypes.TEXT,
				renderFilterValue: (value: string) => value,
				breakPoints: breakPoints,
			},
			DischargingReference: {
				id: "dischargingReference",
				title: intl.formatMessage({ id: "DISCHARGING_REFERENCE" }),
				filterType: FilterTypes.TEXT,
				renderFilterValue: (value: string) => value,
				breakPoints: breakPoints,
			},
			ConnectingVisitLoadingCallReferenceNumber: {
				id: "connectingVisitLoadingCallReferenceNumber",
				title: intl.formatMessage({ id: "CONNECTING_VISIT_LOADING_CALL_REFERENCE" }),
				filterType: FilterTypes.TEXT,
				renderFilterValue: (value: string) => value,
				breakPoints: breakPoints,
			},
			ConnectingVisitDischargingCallReferenceNumber: {
				id: "connectingVisitDischargingCallReferenceNumber",
				title: intl.formatMessage({ id: "CONNECTING_VISIT_DISCHARGING_CALL_REFERENCE" }),
				filterType: FilterTypes.TEXT,
				renderFilterValue: (value: string) => value,
				breakPoints: breakPoints,
			},
			HasDangerousGoods: {
				id: "hasDangerousGoods",
				title: intl.formatMessage({ id: "CARGO_DANGEROUS_GOOD" }),
				filterType: FilterTypes.BOOLEAN,
				renderFilterValue: (value: boolean) => DataFormat.boolean(value),
				breakPoints: breakPoints,
			},
		},
	};
};

export default useCargoTable;
